import { endOfMonth, getDate } from 'date-fns';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Col, Container, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/header';
import { useAuth } from '../../context/AuthContext';
import { useQuestions } from '../../context/QuestionContext';
import { useToast } from '../../context/Toast';
import { getCompanyId } from '../../services/company/company';
import { getUserByEmail } from '../../services/user/user';
import {
  InsideCardStyles,
  StartButton,
  StyledCardSubtitle,
  StyledCardText,
  CardEmployee,
  StyledCardTitleEmployee,
} from './styles';
import { useTranslation } from 'react-i18next';
import { UserStatus } from '../../enums/user';
import { browserSession } from '../../services/utils/browserSession';

const buildAppError = (name: string, message?: string) => {
  const error = new Error(message || name);
  error.name = name;
  return error;
};

const TOKEN_EXPIRED_ERROR = 'Erro ao validar o token - zenbox_backend';

const isExpiredTokenBackendError = (error: any) => {
  return error?.response?.data?.error === TOKEN_EXPIRED_ERROR;
};

// trava em escopo de módulo: sobrevive a remount do componente
let lastProcessedWelcomeSearch: string | null = null;

const Welcome: React.FC = () => {
    const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();

  const { user, saveDataVerifyOnStorage, saveDataSignUpOnStorage, SignOut } = useAuth();
  const { answerId } = useQuestions();
  const { showToast } = useToast();

  const handleExpiredSession = () => {
    showToast(
      t('errors.sessionExpired'),
      'info',
      7500
    );

    setTimeout(() => {
      showToast(
        t('errors.redirectToLogin'),
        'info',
        5000
      );

      setTimeout(() => {
        SignOut();
      }, 5000);
    }, 2500);
  };

  const captureWelcomeIssue = (
    reason: string,
    extra?: Record<string, unknown>,
    originalError?: unknown
  ) => {
    Sentry.withScope((scope) => {
      scope.setTag('module', 'welcome_page');
      scope.setTag('issue_reason', reason);
      scope.setTag('page_path', window.location.pathname);
      scope.setTag('is_iframe', String(window.top !== window.self));

      scope.setContext('welcome_debug', {
        href: window.location.href,
        origin: window.location.origin,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        localStorageAvailable: browserSession.isLocalStorageAvailable(),
        hasVerifyTokenInAuthContext: !!user?.verifyToken,
        hasUserInAuthContext: !!user?.user && Object.keys(user.user || {}).length > 0,
        hasAnswerIdInContext: !!answerId,
        search: location.search,
        ...extra,
      });

      if (originalError instanceof Error) {
        Sentry.captureException(originalError);
      } else {
        Sentry.captureException(buildAppError(reason));
      }
    });
  };

  useEffect(() => {
    const currentSearch = location.search || '';
    const credentialParams = new URLSearchParams(currentSearch);
    credentialParams.delete('locale');
    const credentialSearch = credentialParams.toString();

    if (!credentialSearch) {
      lastProcessedWelcomeSearch = null;
      return;
    }

    // impede reprocessar a mesma query mais de uma vez,
    // mesmo que o componente remonte
    if (lastProcessedWelcomeSearch === credentialSearch) {
      return;
    }

    lastProcessedWelcomeSearch = credentialSearch;

    const getTokenFromUrl = async () => {
      try {
        const queryParams = new URLSearchParams(currentSearch);
        const url: any = {};

        const token = queryParams.get('token');
        const email = queryParams.get('email');
        const confirmationCode = queryParams.get('confirmation_code');

        if (token) {
          url.verifyToken = token;
        }

        if (email) {
          url.user = {
            email,
          };
        }

        if (confirmationCode) {
          url.confirmationCode = confirmationCode;
        }

        if (Object.keys(url).length === 0) {
          return;
        }

        await saveDataVerifyOnStorage(url);

        if (email) {
          let result = null;

          try {
            result = await getUserByEmail(email);
          } catch (error: any) {
            if (isExpiredTokenBackendError(error)) {
              handleExpiredSession();
              return;
            }

            captureWelcomeIssue(
              'failed_to_fetch_user_by_email_on_welcome',
              {
                email,
                hasTokenInQuery: !!token,
                hasConfirmationCodeInQuery: !!confirmationCode,
                responseStatus: error?.response?.status ?? null,
                responseError: error?.response?.data?.error ?? null,
              },
              error
            );

            return;
          }

          if (result) {
            const dataToSave = {
              id: result?.id,
              email: result?.email,
              status: result?.status,
              first_name: result?.first_name,
              last_name: result?.last_name,
              role: result?.role,
              user_company: result?.company?.Item,
              company_visited: (await getCompanyId(result?.company?.Item?.id))?.Item,
            };

            await saveDataSignUpOnStorage(dataToSave);
          } else {
            captureWelcomeIssue('user_not_found_by_email_on_welcome', {
              email,
              hasTokenInQuery: !!token,
              hasConfirmationCodeInQuery: !!confirmationCode,
            });
          }
        }
      } catch (error: any) {
        if (isExpiredTokenBackendError(error)) {
          handleExpiredSession();
          return;
        }

        captureWelcomeIssue(
          'failed_to_process_query_params_on_welcome',
          {
            search: currentSearch,
            responseStatus: error?.response?.status ?? null,
            responseError: error?.response?.data?.error ?? null,
          },
          error
        );
      }
    };

    getTokenFromUrl();
  }, [location.search, saveDataVerifyOnStorage, saveDataSignUpOnStorage]);

  return (
    <>
      <Header />

      <Container fluid className="d-flex h-100 mt-5 mb-2">
        <Row className="justify-content-center align-self-center w-100">
          <Col md={9} lg={7}>
            <CardEmployee>
              <InsideCardStyles className=" d-flex flex-column align-items-center">
                <StyledCardTitleEmployee>
                  {t('onboarding.titleLine1')}
                  <br />
                  {t('onboarding.titleLine2')}
                </StyledCardTitleEmployee>

                <StyledCardSubtitle className="mt-4">
                  {t('onboarding.subtitle')}
                </StyledCardSubtitle>

                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    marginBottom: 20,
                  }}
                >
                  <StyledCardText>
                    {t('onboarding.introParagraph1')}
                    <br />
                    <br />
                    {t('onboarding.introParagraph2')}
                    <br />
                    <br />
                    {t('onboarding.start')}
                  </StyledCardText>
                </div>

                <StartButton
                  onClick={async () => {
                    const resolvedVerifyToken = user?.verifyToken ?? null;
                    const resolvedUser = user?.user ?? {};
                    const resolvedCompanyVisited = user?.company_visited ?? null;

                    if (resolvedVerifyToken) {
                      try {
                        if (
                          !resolvedUser ||
                          Object.keys(resolvedUser).length === 0 ||
                          !resolvedCompanyVisited?.id ||
                          !resolvedUser.email
                        ) {
                          captureWelcomeIssue('incomplete_user_data_before_start_questions', {
                            hasResolvedUser: !!resolvedUser,
                            resolvedUserKeys: Object.keys(resolvedUser || {}),
                            hasCompanyId: !!resolvedCompanyVisited?.id,
                            hasEmail: !!resolvedUser?.email,
                            hasVerifyToken: !!resolvedVerifyToken,
                          });
                          // handleExpiredSession();
                          return;
                        }

                        const company = await getCompanyId(resolvedCompanyVisited?.id);
                        const updatedStatus = resolvedUser.status;

                        if (
                          updatedStatus !== UserStatus.active &&
                          updatedStatus !== UserStatus.invited
                        ) {
                          showToast(
                            'Sua conta está inativada.\n\nPara reativá-la peça ao Gestor de Felicidade da sua organização ou fale com nosso suporte pelo botão de ajuda no canto inferior direito da tela.',
                            'info',
                            18000
                          );
                          return;
                        }

                        if (!company.Item.surveys_all_month) {
                          const today = new Date();
                          const day = getDate(today);
                          const lastDayOfMonth = getDate(endOfMonth(today));

                          if (!(day >= 20 && day <= lastDayOfMonth)) {
                            showToast(
                              'Estamos fora do periodo de reflexão :(\n\nPara responder a reflexão do mês, você deve acessar durante o período do dia 20 até o último dia do mês.',
                              'info',
                              20000
                            );
                          } else {
                            navigate('/questions');
                          }
                        } else {
                          navigate('/questions');
                        }
                      } catch (error: any) {
                        if (isExpiredTokenBackendError(error)) {
                          handleExpiredSession();
                          return;
                        }

                        captureWelcomeIssue(
                          'failed_on_start_button_flow',
                          {
                            errorName: error?.name ?? null,
                            errorMessage: error?.message ?? null,
                            hasVerifyToken: !!resolvedVerifyToken,
                            hasResolvedUser: !!resolvedUser,
                            hasCompanyId: !!resolvedCompanyVisited?.id,
                            hasEmail: !!resolvedUser?.email,
                            responseStatus: error?.response?.status ?? null,
                            responseError: error?.response?.data?.error ?? null,
                          },
                          error
                        );

                        if (error.response && error.response.data && error.response.data.error) {
                          switch (error?.response?.data?.error) {
                            case TOKEN_EXPIRED_ERROR:
                              handleExpiredSession();
                              break;

                            default:
                              showToast(
                                'Ops! Algo não correu bem :(\n\nNosso time de engenheiros foi notificado e iremos corrigir essa questão o quanto antes. Para falar com nosso suporte, utilize o botão "Ajuda" na parte inferior da tela. ',
                                'error',
                                30000
                              );
                              throw error;
                          }
                        } else {
                          showToast(
                            'Ops! Algo não correu bem :(\n\nNosso time de engenheiros foi notificado e iremos corrigir essa questão o quanto antes. Para falar com nosso suporte, utilize o botão "Ajuda" na parte inferior da tela. ',
                            'error',
                            30000
                          );
                          throw error;
                        }
                      }
                    } else {
                      navigate('/signup');
                    }
                  }}
                >
                  Começar
                </StartButton>
              </InsideCardStyles>
            </CardEmployee>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Welcome;
